/**
 * Trigger file download from a blob.
 * Robust against Emergent iframe sandbox restrictions where the
 * anchor `download` attribute can be silently ignored.
 *
 * Strategy:
 *  1. Try classic anchor[download] click (works in non-iframe contexts).
 *  2. If running inside an iframe, ALSO open the blob URL in a new top-level
 *     window — browsers will trigger a Save-As dialog for binary mime types
 *     and render inline for PDFs.
 */
export const downloadBlob = (data, filename, mimeType = 'application/octet-stream') => {
  const blob = new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const inIframe = (() => { try { return window.self !== window.top; } catch (e) { return true; } })();

  // 1) Classic anchor download (works outside iframes & in some iframe configs)
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();

  // 2) Iframe fallback — open blob URL in a new tab.
  // Browser triggers a download for unknown mime types and renders PDFs inline.
  if (inIframe) {
    try {
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      // If popup blocked, force top-level navigation as last resort
      if (!w) {
        try { window.top.location.href = url; } catch (_) { /* cross-origin */ }
      }
    } catch (e) { /* ignore */ }
  }

  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};
